import {
  Alert,
  AlertTitle,
  Button,
  Grid2,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation, Trans } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import RecurlyCardElement from 'components/RecurlyBillingInformation/RecurlyCardElement';
import { Controller, useForm } from 'react-hook-form';
import { generateClientToken } from 'shared/utils';
import { useRecurly } from '@recurly/react-recurly';
import { getCreditCardValidationErrors } from 'components/RecurlyBillingInformation/utils';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { useRef, useState } from 'react';
import * as Sentry from '@sentry/capacitor';
import { useAuthState } from 'state/useAuthState';
import {
  isRecurlyError,
  parseRecurlyErrorToTransKey,
} from 'shared/recurlyUtils';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { RecurlySavedPayment } from 'components/RecurlySavedPayment';
import { useSavedPaymentMethod } from 'hooks/useSavedPaymentMethod';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { useHistory, useParams } from 'react-router-dom';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { TERMS_OF_SERVICE, PRIVACY_POLICY } from '../../../../constants';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';

interface OrganizationBillingData {
  companyName: string;
  postalCode: string;
  token?: string;
  acceptedTerms: boolean;
}

type OrganizationBillingInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationBillingInfoForm = (
  props: OrganizationBillingInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const recurly = useRecurly();
  const [errors, setErrors] = useState<string[]>([]);
  const { processOrganizationPurchase } = useOrganizationPurchase({});
  const orgState = useOrganizationState();
  const { user } = useAuthState();
  const { new: useNewPaymentMethod } = useParams<{ new?: string }>();
  const history = useHistory();

  const { previewPurchase } = useOrganizationPurchase({
    processOrganizationPurchase: {
      regularSeats: orgState.regularSeats,
      proSeats: orgState.proSeats,
    },
  });

  const handleToggleUseNewPaymentMethod = (): void => {
    if (useNewPaymentMethod) {
      history.replace('/organization/purchase/billing');
    } else {
      history.push('/organization/purchase/billing/new');
    }
    setErrors([]);
  };

  const { savedPaymentMethod, isLoading: isLoadingPaymentMethod } =
    useSavedPaymentMethod({
      useLoggedInState: true,
      paymentType: 'credit_card',
    });

  const usingSavedPaymentMethod = savedPaymentMethod && !useNewPaymentMethod;

  const { control, handleSubmit, formState } = useForm<OrganizationBillingData>(
    {
      resolver: yupResolver(
        getOrgCommonSignupFormsValidationSchema({
          t,
          requireExistingUserToLogin: Boolean(user),
          emailValidationCache: new Map<string, boolean>(),
          fields: usingSavedPaymentMethod
            ? ['acceptedTerms'] // No need to validate postal code if using saved payment method
            : ['postalCode', 'acceptedTerms'],
        }),
      ),
      defaultValues: {
        postalCode: '',
        acceptedTerms: false,
      },
      mode: 'onBlur',
    },
  );

  const clientToken = useRef<string>(user ? '' : generateClientToken());

  const hasErrors = errors.length > 0;

  const submitToken = async (
    values: OrganizationBillingData,
    authorizationToken?: string,
  ): Promise<void> => {
    await processOrganizationPurchase({
      companyName: orgState.companyName,
      firstName: orgState.firstName,
      lastName: orgState.lastName,
      email: orgState.email,
      proSeats: orgState.proSeats,
      regularSeats: orgState.regularSeats,
      poNumber: orgState.poNumber,
      postalCode: values.postalCode,
      acceptedTerms: values.acceptedTerms,
      ...(authorizationToken ? { authorizationToken } : {}),
      ...(clientToken.current ? { clientToken: clientToken.current } : {}),
    });

    onSubmit({
      ...orgState,
      ...(clientToken.current ? { clientToken: clientToken.current } : {}),
    });
  };

  const handleSubmissionError = (error: unknown): void => {
    Sentry.captureException(error);
    if (error && isRecurlyError(error)) {
      const validationErrors = getCreditCardValidationErrors(error, t);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
    }
    const key = parseRecurlyErrorToTransKey(error);
    if (key) {
      setErrors([t(key)]);
      return;
    }

    logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
  };

  const handleFormSubmit = async (
    values: OrganizationBillingData,
  ): Promise<void> => {
    try {
      if (usingSavedPaymentMethod) {
        await submitToken(values);
      } else {
        const token = await new Promise<string>((resolve, reject) => {
          recurly.token(
            {
              first_name: orgState.firstName,
              last_name: orgState.lastName,
              postal_code: values.postalCode,
            },
            (err, recurlyToken) => {
              if (err) {
                const validationErrors = getCreditCardValidationErrors(err, t);
                setErrors(validationErrors);
                reject(err);
              } else {
                resolve(recurlyToken.id);
              }
            },
          );
        });
        await submitToken(values, token);
      }
    } catch (error) {
      handleSubmissionError(error);
    }
  };

  if (isLoadingPaymentMethod || !previewPurchase) {
    return <LoadingAndErrors isLoading={isLoadingPaymentMethod} />;
  }

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Typography variant="h3">
            <b>{t('organizationPurchase.billingInfo')}</b>
          </Typography>
        </Grid2>
        {hasErrors && (
          <Grid2>
            <Alert icon={false} severity="error" sx={{ color: 'error.main' }}>
              <AlertTitle>{t('recurlyBillingInformation.error')}</AlertTitle>
              <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
                {errors.map((error) => (
                  <ListItem
                    key={error}
                    dense
                    disablePadding
                    sx={{ display: 'list-item' }}
                  >
                    <ListItemText
                      primary={error}
                      sx={{ margin: 0 }}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Grid2>
        )}
        {usingSavedPaymentMethod ? (
          <>
            <Grid2>
              <RecurlySavedPayment paymentMethod={savedPaymentMethod} />
            </Grid2>
            {/* Using structure from existing RecurlyAccountInformation page */}
            <Grid2>
              <Typography variant="h3" sx={{ marginTop: 4 }}>
                {t('organizationPurchase.updatePaymentMethod')}
              </Typography>
            </Grid2>

            <Grid2>
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleToggleUseNewPaymentMethod}>
                    <ListItemText
                      primary={t('recurlyAccountInformation.button')}
                    />
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <ArrowForwardIosIcon color="inherit" />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </List>
            </Grid2>
          </>
        ) : (
          <>
            <Grid2>
              <RecurlyCardElement error={hasErrors} />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label={t('organizationPurchase.zipcode.label')}
                    required
                    error={Boolean(formState.errors.postalCode)}
                    helperText={formState.errors.postalCode?.message}
                    {...field}
                  />
                )}
              />
            </Grid2>
          </>
        )}
        <Grid2>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid2>

        <OrganizationPurchaseSummary
          showPerPlanType
          regularSeats={orgState.regularSeats}
          proSeats={orgState.proSeats}
        />
      </Grid2>
      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Stack spacing={2}>
          <Controller
            name="acceptedTerms"
            control={control}
            render={({ field }) => (
              <label htmlFor="acceptedTerms">
                <input
                  id="acceptedTerms"
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  required
                  name={field.name}
                  ref={field.ref}
                />
                <Trans i18nKey="organizationPurchase.acceptTerms.label">
                  I agree tothe
                  <a
                    href={TERMS_OF_SERVICE}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    Terms of Service
                  </a>
                  and
                  <a
                    href={PRIVACY_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    Privacy Policy
                  </a>
                </Trans>
              </label>
            )}
          />
          <Button
            size="large"
            fullWidth
            type="submit"
            disabled={!formState.isValid || formState.isSubmitting}
          >
            {t('organizationPurchase.purchase')}
          </Button>
        </Stack>
      </Grid2>
    </Grid2>
  );
};
